import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Modal, Dropdown, Tag, TextArea } from "pepsico-ds";
import { useToast } from "../Toast/ToastContext";
import Vector from "../../assets/Vector.png";
import { FetchShareListAction, ShareRisiNewsEmailAction } from "../../redux/action/RISIAction";

/* istanbul ignore next */
function filterByKeyword(list, keyword) {
    if (!keyword) return list.slice(); // return copy if no keyword
    const q = String(keyword).toLowerCase();
    return list.filter(({ displayText }) =>
        String(displayText).toLowerCase().includes(q)
    );
}

/* istanbul ignore next */
function validateCompanyEmail(email) {

    if (typeof email !== 'string' || email.trim() === '') {
        return { isValid: false, reason: 'Email cannot be empty' };
    }

    if (!email.toLowerCase().endsWith('@pepsico.com')) {
        return { isValid: false, reason: 'Email must end with @pepsico.com' };
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@pepsico\.com$/i;
    if (!emailRegex.test(email)) {
        return { isValid: false, reason: 'Email format is invalid' };
    }
    const username = email.split('@')[0];
    if (username.length < 3) {
        return { isValid: false, reason: 'Username must be at least 3 characters long' };
    }
    if (username.length > 64) {
        return { isValid: false, reason: 'Username cannot exceed 64 characters' };
    }
    if (/\.{2,}|-{2,}|_{2,}|(\.-)|(\._)|(-\. )|(-_)|(_\.)|(_-)/g.test(username)) {
        return { isValid: false, reason: 'Username cannot contain consecutive special characters' };
    }
    if (/^(?:[._-].*|.*[._-])$/.test(username)) {
        return { isValid: false, reason: 'Username cannot start or end with a special character' };
    }

    return { isValid: true, reason: 'Email is valid' };
}

/**
 * Reusable "Share this article" modal.
 * Shared between the News detail page and each News card on the overview page
 * so the user list / recent list is fetched once and the share logic lives
 * in a single place.
 */
function ShareNewsModal({ visible, onClose, newsItem, userEmail }) {
    const dispatch = useDispatch();
    const { showToast } = useToast();

    const shareListData = useSelector((state) => state.getShareListReducer);
    const shareNewsEmailResponseData = useSelector((state) => state.getShareNewsEmailReducer);

    const [recentUsers, setRecentUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [appDomain, setAppDomain] = useState("");

    const [selectedValue, setSelectedValue] = useState([]);
    const [childList, setChildList] = useState([]);
    const [emailDropdownHeader, setEmailDropdownHeader] = useState("");
    const [finalEmailList, setFinalEmailList] = useState([]);
    const [sharenewsinput, setSharenewsinput] = useState("");
    const [sharenewsinputCount, setSharenewsinputCount] = useState(0);

    /* istanbul ignore next */
    useEffect(() => {
        if (userEmail) {
            dispatch(FetchShareListAction(userEmail));
        }
    }, [userEmail]);

    /* istanbul ignore next */
    useEffect(() => {
        if (shareListData && shareListData.type === "SUCCESS_SHARE_LIST_ACTION") {
            const users = shareListData?.email?.map((item, index) => ({ displayText: item, id: index + 1 })) || [];
            setAllUsers(users);
            const recentList = shareListData?.lastFiveSharedEmail?.map((item, index) => ({ displayText: item, id: index + 1 })) || [];
            setRecentUsers(recentList);
            setAppDomain(shareListData?.url);
        }
    }, [shareListData]);

    /* istanbul ignore next */
    useEffect(() => {
        if (userEmail && shareNewsEmailResponseData && shareNewsEmailResponseData.type === "SUCCESS_SHARE_NEWS_EMAIL_ACTION") {
            dispatch(FetchShareListAction(userEmail));
        }
    }, [shareNewsEmailResponseData]);

    /* istanbul ignore next */
    useEffect(() => {
        if (visible) {
            setSharenewsinput("");
            setSharenewsinputCount(0);
            setFinalEmailList([]);
            setSelectedValue([]);
            if (recentUsers && recentUsers?.length > 0) {
                setEmailDropdownHeader("Recent User List");
                setChildList(recentUsers);
            } else {
                setEmailDropdownHeader("PH User List");
                setChildList(allUsers);
            }
        }
    }, [visible]);

    /* istanbul ignore next */
    const addToFinalEmailList = (val) => {
        if (val && val.length > 0) {
            const email = val[0]?.displayText;
            setFinalEmailList(prev => {
                const emailExists = prev.includes(email);
                return emailExists ? prev : [...prev, email];
            });
        }
    }

    /* istanbul ignore next */
    const removeEmailFromList = (event) => {
        const val = event;
        const newEmailList = finalEmailList.filter(x => x !== val);
        setFinalEmailList(newEmailList)
    }

    /* istanbul ignore next */
    const adjustEmailOptions = (val) => {
        if (val && val.length > 0) {
            let subList = filterByKeyword(allUsers, val)
            let dropdownHeader = "PH User List";
            if ((!subList || subList.length === 0)
                && validateCompanyEmail(val)?.isValid) {
                subList = [
                    {
                        id: 1,
                        displayText: val
                    }
                ]
                dropdownHeader = "Add User";
            }
            setEmailDropdownHeader(dropdownHeader)
            setChildList(subList)
        } else {
            if (recentUsers && recentUsers?.length > 0) {
                setEmailDropdownHeader("Recent User List")
                setChildList(recentUsers)
            } else {
                setEmailDropdownHeader("PH User List")
                setChildList(allUsers)
            }
        }
    }

    /* istanbul ignore next */
    const sendEmail = () => {
        let paramReq = {
            "articleId": newsItem?.id,
            "fromEmailId": userEmail,
            "toEmailIds": finalEmailList,
            "message": sharenewsinput,
            "source": newsItem?.source
        }
        dispatch(ShareRisiNewsEmailAction(paramReq))
        showToast('success', "Email sent and article shared successfully", {
            icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
        });
        onClose();
    }

    /* istanbul ignore next */
    const copyLink = async () => {
        try {
            if(newsItem?.link) {
                await navigator.clipboard.writeText(newsItem?.link);
            } else {
                await navigator.clipboard.writeText(appDomain + "/news-detail/" + newsItem?.id);
            }
            showToast('success', "Link copied to clipboard", {
                icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
            });
        } catch (err) {
            console.log("Failed to copy");
        }
    };

    if (!visible) {
        return null;
    }

    /* istanbul ignore next */
    return (
        <div>
            <Modal
                className="share-news-modal"
                onCloseModal={onClose}
                primaryButtonProps={{
                    className: "ph-news-send-button",
                    onClick: sendEmail,
                    size: 'medium',
                    text: 'Send',
                    variant: 'primary',
                    iconLeading: "send",
                    disabled: finalEmailList?.length == 0 ? true : false
                }}
                secondaryButtonProps={{
                    onClick: () => { copyLink() },
                    iconLeading: "link",
                    size: 'medium',
                    text: 'Copy Link',
                    variant: 'secondary'
                }}
                showPrimaryButton
                showLink={false}
                showAuxiliaryButton={false}
                showSecondaryButton
                showTertiaryButton={false}
                showSlot={true}
                showModal={visible}
                closeOnOutsideClick={false}
                hideCloseButton={false}
                type="M"
                title="Share this article"
            >
                <span className="sidepanel-sub-header">PepsiCo email domains only.</span>
                <br />
                <Card className="share-news-card">
                    <div style={{ width: '100%' }}>
                        <Dropdown
                            childList={childList}
                            dropdownHeader={emailDropdownHeader}
                            isClearable={true}
                            placeholder={"User Email List"}
                            selection="single"
                            size="medium"
                            className={"share-news-dropdown"}
                            selectedValue={selectedValue}
                            setSelectedValue={val => addToFinalEmailList(val)}
                            onSearchInputChange={adjustEmailOptions}
                            hideMenuListOnSelection={false}
                            label={"Email List"}
                            showLabelIcon={false}
                            style={{ width: '500px !important' }}
                        />
                    </div>
                </Card>
                <br />
                {
                    finalEmailList && finalEmailList.length > 0 &&
                    <>
                        <Card>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignSelf: "baseline" }}>
                                {finalEmailList.map((text, idx) => (
                                    <Tag
                                        key={`${text}-${idx}`}
                                        className=""
                                        color="default"
                                        isRemovable
                                        size="large"
                                        style={{}}
                                        text={text}
                                        type="default"
                                        onRemove={() => removeEmailFromList(text)}
                                    />
                                ))}
                            </div>
                        </Card>
                        <br />
                    </>
                }
                <div>
                    <Card style={{ minHeight: '150px' }}>
                        <div style={{ width: '100%' }}>
                            <TextArea
                                disableResize={true}
                                feedbackType={sharenewsinputCount > 200 ? "error" : "info"}
                                helperStyle={{}}
                                helperText={`(${sharenewsinputCount}/200)`}
                                label=""
                                onChange={(e) => {
                                    setSharenewsinput(e.target.value);
                                    setSharenewsinputCount(e.target.value.length);
                                }}
                                placeholder="Add a message (optional)"
                                showCharacterCount={false}
                                showLabelIcon={false}
                                showToolbar={false}
                                style={{
                                    overflow: 'hidden',
                                    minHeight: '75px',
                                    maxWidth: '40rem !important'
                                }}
                                maxLength={200}
                                value={sharenewsinput}
                                className={"share-news-textarea"}
                            />
                        </div>
                    </Card>
                </div>
                <br />
                <br />
            </Modal>
        </div>
    );
}

export default ShareNewsModal;
